import { useState, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getAgents,
  createAgent,
  updateAgent,
  deleteAgent as deleteAgentApi,
  testQuery,
  type Agent as ApiAgent,
} from '@/api/agents';
import { type AgentConfig } from '@/components/ai/AgentBuilder';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Sparkles,
  Bot,
  Plus,
  MessageSquare,
  Send,
  Loader2,
  Database,
  CheckCircle2,
  AlertCircle,
  ChevronRight,
  Lightbulb,
  ExternalLink,
  Play,
  Pause,
  Trash2,
  Clock,
  Mail,
  Webhook,
  MoreHorizontal,
  Zap,
  ArrowRight,
  History,
  Search,
  Copy,
  Check,
  Settings2,
  X,
  Pencil,
  Paperclip,
  FileText,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { cn } from '@/lib/utils';
import { getBranches, getOdooUsers } from '@/api/deployments';
import { getAIConversations, deleteAIConversation, getAIConversationMessages, updateAIConversationTitle, type AIConversation, type AIMessage } from '@/api/project';
import { useOdooAgent, type Message, type ToolCall, type AssistantTurn, type OdooModelFull, type TokenUsage, type ImageAttachment, type TextFileAttachment } from '@/hooks/useOdooAgent';
import { AgentBuilder } from '@/components/ai/AgentBuilder';
import { ChartRenderer, parseChartsFromMarkdown } from '@/components/ai/ChartRenderer';
import { getKnowledgeStatus, indexKnowledgeBase } from '@/api/knowledge';
import { KnowledgeViewer } from '@/components/ai/KnowledgeViewer';
import { toast } from 'sonner';

interface Deployment {
  buildId: number;
  branchName: string;
  branchType: 'production' | 'staging' | 'development';
  fqdn: string | null;
}

interface ChatHistory {
  id: string;
  title: string;
  preview: string;
  timestamp: Date;
}

// Agent type alias for display purposes - maps API agent to display format
interface DisplayAgent {
  id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  triggerType: 'cron' | 'webhook' | 'manual';
  cronExpression?: string;
  actionType: 'email' | 'webhook';
  lastRunAt: Date | null;
  lastRunStatus: 'success' | 'failed' | 'skipped' | null;
}

function mapApiAgentToDisplay(agent: ApiAgent): DisplayAgent {
  return {
    id: agent.id,
    name: agent.name,
    description: agent.description,
    enabled: agent.enabled,
    triggerType: agent.trigger_type,
    cronExpression: agent.trigger_config.cronExpression,
    actionType: agent.action_type,
    lastRunAt: agent.last_run_at ? new Date(agent.last_run_at) : null,
    lastRunStatus: agent.last_run_status,
  };
}


interface AvailableColumn {
  name: string;
  type: string;
  table: string;
  label: string | Record<string, string>;
  isRelation: boolean;
  relatedTable?: string;
}

interface QueryStructure {
  columns: string;
  fromClause: string;
  joins: string;
  whereClause: string;
  orderBy: string;
  limit: string;
}

interface ColumnSelectorModalProps {
  isOpen: boolean;
  availableColumns: AvailableColumn[];
  queryStructure: QueryStructure;
  primaryTable: string;
  onRefine: (columns: string[]) => void;
  onClose: () => void;
}

function ColumnSelectorModal({ isOpen, availableColumns, queryStructure, primaryTable, onRefine, onClose }: ColumnSelectorModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(() => {
    const currentCols = queryStructure.columns.split(',').map(c => c.trim());
    return new Set(currentCols);
  });
  const [expandedTables, setExpandedTables] = useState<Set<string>>(() => {
    // Start with all tables expanded
    const tables = new Set<string>();
    availableColumns.forEach(col => tables.add(col.table));
    return tables;
  });

  // Group columns by table
  const columnsByTable = availableColumns.reduce((acc, col) => {
    if (!acc[col.table]) acc[col.table] = [];
    acc[col.table].push(col);
    return acc;
  }, {} as Record<string, AvailableColumn[]>);

  // Filter columns based on search
  const filteredColumnsByTable = Object.entries(columnsByTable).reduce((acc, [table, cols]) => {
    if (!searchQuery) {
      acc[table] = cols;
      return acc;
    }
    const query = searchQuery.toLowerCase();
    const filtered = cols.filter(col => {
      const label = typeof col.label === 'string' ? col.label : (col.label && typeof col.label === 'object' ? Object.values(col.label)[0] : col.name);
      return col.name.toLowerCase().includes(query) || 
             label.toLowerCase().includes(query) ||
             col.type.toLowerCase().includes(query) ||
             table.toLowerCase().includes(query);
    });
    if (filtered.length > 0) {
      acc[table] = filtered;
    }
    return acc;
  }, {} as Record<string, AvailableColumn[]>);

  const toggleColumn = (columnName: string, tableName: string) => {
    const fullName = tableName === primaryTable ? columnName : `${tableName}.${columnName}`;
    setSelectedColumns(prev => {
      const next = new Set(prev);
      if (next.has(fullName) || next.has(columnName)) {
        next.delete(fullName);
        next.delete(columnName);
      } else {
        next.add(fullName);
      }
      return next;
    });
  };

  const isSelected = (columnName: string, tableName: string) => {
    const fullName = tableName === primaryTable ? columnName : `${tableName}.${columnName}`;
    return selectedColumns.has(fullName) || selectedColumns.has(columnName);
  };

  const toggleTable = (tableName: string) => {
    setExpandedTables(prev => {
      const next = new Set(prev);
      if (next.has(tableName)) {
        next.delete(tableName);
      } else {
        next.add(tableName);
      }
      return next;
    });
  };

  const selectAllInTable = (tableName: string, columns: AvailableColumn[]) => {
    setSelectedColumns(prev => {
      const next = new Set(prev);
      columns.forEach(col => {
        const fullName = tableName === primaryTable ? col.name : `${tableName}.${col.name}`;
        next.add(fullName);
      });
      return next;
    });
  };

  const deselectAllInTable = (tableName: string, columns: AvailableColumn[]) => {
    setSelectedColumns(prev => {
      const next = new Set(prev);
      columns.forEach(col => {
        const fullName = tableName === primaryTable ? col.name : `${tableName}.${col.name}`;
        next.delete(fullName);
        next.delete(col.name);
      });
      return next;
    });
  };

  const getSelectedCountInTable = (tableName: string, columns: AvailableColumn[]) => {
    return columns.filter(col => isSelected(col.name, tableName)).length;
  };

  const handleApply = () => {
    if (selectedColumns.size === 0) return;
    onRefine(Array.from(selectedColumns));
    onClose();
  };

  const getColumnLabel = (col: AvailableColumn) => {
    if (typeof col.label === 'string') return col.label;
    if (col.label && typeof col.label === 'object') return Object.values(col.label)[0] || col.name;
    return col.name;
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'many2one':
      case 'many2many':
        return 'bg-blue-100 text-blue-700';
      case 'integer':
      case 'float':
      case 'monetary':
        return 'bg-emerald-100 text-emerald-700';
      case 'date':
      case 'datetime':
        return 'bg-amber-100 text-amber-700';
      case 'boolean':
        return 'bg-purple-100 text-purple-700';
      case 'char':
      case 'text':
      case 'html':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const totalColumns = availableColumns.length;
  const totalTables = Object.keys(columnsByTable).length;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0">
        <VisuallyHidden.Root>
          <DialogTitle>Select Columns</DialogTitle>
        </VisuallyHidden.Root>
        
        {/* Header */}
        <div className="px-6 py-4 border-b bg-gradient-to-r from-violet-50 to-purple-50">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Customize Columns</h2>
              <p className="text-sm text-muted-foreground">
                {totalColumns} columns available across {totalTables} table{totalTables !== 1 ? 's' : ''}
              </p>
            </div>
            <Badge variant="secondary" className="text-sm px-3 py-1">
              {selectedColumns.size} selected
            </Badge>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search columns by name, label, or type..."
              className="pl-9 bg-white"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Column List */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {Object.keys(filteredColumnsByTable).length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Search className="h-12 w-12 text-muted-foreground/30 mb-3" />
              <p className="text-muted-foreground">No columns match "{searchQuery}"</p>
              <button 
                onClick={() => setSearchQuery('')}
                className="text-sm text-primary hover:underline mt-2"
              >
                Clear search
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {Object.entries(filteredColumnsByTable).map(([tableName, columns]) => {
                const isExpanded = expandedTables.has(tableName);
                const selectedCount = getSelectedCountInTable(tableName, columns);
                const isPrimaryTable = tableName === primaryTable;
                
                return (
                  <div key={tableName} className="border rounded-lg overflow-hidden">
                    {/* Table Header */}
                    <button
                      onClick={() => toggleTable(tableName)}
                      className={cn(
                        "w-full flex items-center justify-between px-4 py-3 text-left transition-colors",
                        isPrimaryTable ? "bg-violet-50 hover:bg-violet-100" : "bg-muted/50 hover:bg-muted"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <ChevronRight className={cn(
                          "h-4 w-4 text-muted-foreground transition-transform",
                          isExpanded && "rotate-90"
                        )} />
                        <div>
                          <div className="flex items-center gap-2">
                            <Database className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{tableName}</span>
                            {isPrimaryTable && (
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">Primary</Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {columns.length} column{columns.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {selectedCount > 0 && (
                          <Badge className="bg-primary/10 text-primary hover:bg-primary/20">
                            {selectedCount} selected
                          </Badge>
                        )}
                      </div>
                    </button>
                    
                    {/* Columns */}
                    {isExpanded && (
                      <div className="border-t">
                        {/* Quick Actions */}
                        <div className="px-4 py-2 bg-muted/30 border-b flex items-center gap-2 text-xs">
                          <button
                            onClick={() => selectAllInTable(tableName, columns)}
                            className="text-primary hover:underline"
                          >
                            Select all
                          </button>
                          <span className="text-muted-foreground">•</span>
                          <button
                            onClick={() => deselectAllInTable(tableName, columns)}
                            className="text-muted-foreground hover:text-foreground hover:underline"
                          >
                            Deselect all
                          </button>
                        </div>
                        
                        {/* Column Grid */}
                        <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-1">
                          {columns.map(col => {
                            const selected = isSelected(col.name, tableName);
                            const label = getColumnLabel(col);
                            
                            return (
                              <label
                                key={`${tableName}.${col.name}`}
                                className={cn(
                                  "flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all",
                                  selected 
                                    ? "bg-primary/5 border border-primary/20" 
                                    : "hover:bg-muted/50 border border-transparent"
                                )}
                              >
                                <Checkbox
                                  checked={selected}
                                  onCheckedChange={() => toggleColumn(col.name, tableName)}
                                  className="h-4 w-4"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className={cn(
                                      "font-medium text-sm truncate",
                                      selected && "text-primary"
                                    )}>
                                      {label}
                                    </span>
                                    {col.isRelation && (
                                      <ArrowRight className="h-3 w-3 text-blue-500 flex-shrink-0" />
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <code className="text-[10px] text-muted-foreground font-mono">
                                      {col.name}
                                    </code>
                                    <span className={cn(
                                      "text-[10px] px-1.5 py-0.5 rounded-full",
                                      getTypeColor(col.type)
                                    )}>
                                      {col.type}
                                    </span>
                                    {col.relatedTable && (
                                      <span className="text-[10px] text-blue-600">
                                        → {col.relatedTable}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-muted/30 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {selectedColumns.size === 0 
              ? "Select at least one column" 
              : `${selectedColumns.size} column${selectedColumns.size !== 1 ? 's' : ''} will be included in your query`
            }
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleApply} 
              disabled={selectedColumns.size === 0}
              className="gap-2"
            >
              <Check className="h-4 w-4" />
              Apply Changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface ToolCallCardProps {
  toolCall: ToolCall;
  onRefineQuery?: (
    baseQuery: { primaryTable: string; joins?: string; conditions?: string; orderBy?: string; limit?: number },
    selectedColumns: string[]
  ) => void;
}

function ToolCallCard({ toolCall, onRefineQuery }: ToolCallCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const result = toolCall.result as Record<string, unknown> | undefined;
  const hasError = !!result?.error;
  const isLoading = toolCall.status === 'running';

  const availableColumns = (result?.available_columns as AvailableColumn[]) || [];
  const queryStructure = result?.query_structure as QueryStructure | undefined;
  const primaryTable = (result?.primary_table as string) || '';

  const handleRefine = (columns: string[]) => {
    if (!queryStructure || !primaryTable || !onRefineQuery) return;

    onRefineQuery(
      {
        primaryTable,
        joins: queryStructure.joins || undefined,
        conditions: queryStructure.whereClause || undefined,
        orderBy: queryStructure.orderBy || undefined,
        limit: queryStructure.limit ? parseInt(queryStructure.limit) : undefined,
      },
      columns
    );
    setShowColumnSelector(false);
  };

  // Get display name and icon for tool
  const getToolDisplay = () => {
    switch (toolCall.name) {
      case 'list_tables':
        return { name: 'Listing tables', icon: <Database className="h-4 w-4" /> };
      case 'describe_table':
        return { name: `Describing ${toolCall.args?.table_name || 'table'}`, icon: <Database className="h-4 w-4" /> };
      case 'sample_values':
        return { name: `Sampling ${toolCall.args?.column_name || 'column'}`, icon: <Search className="h-4 w-4" /> };
      case 'query_odoo':
        return { name: 'Database query', icon: <Database className="h-4 w-4" /> };
      case 'generate_chart':
        return { name: 'Generating chart', icon: <Sparkles className="h-4 w-4" /> };
      default:
        return { name: toolCall.name, icon: <Database className="h-4 w-4" /> };
    }
  };

  const toolDisplay = getToolDisplay();

  // Render tool-specific content
  const renderToolContent = () => {
    if (hasError) {
      return (
        <div className="text-red-600">
          <p className="font-medium text-xs uppercase">Error</p>
          <p className="mt-1">{String(result?.error ?? '')}</p>
          {result?.suggestion ? (
            <p className="mt-2 text-xs text-amber-700 bg-amber-50 p-2 rounded border border-amber-200">
              💡 {String(result.suggestion)}
            </p>
          ) : null}
        </div>
      );
    }

    switch (toolCall.name) {
      case 'list_tables': {
        const tables = (result?.tables as Array<{ table_name: string; approx_rows: number }>) || [];
        return (
          <div>
            <p className="text-muted-foreground text-xs mb-2">
              Found {String(result?.count ?? tables.length)} tables {result?.note ? `(${String(result.note)})` : ''}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
              {tables.slice(0, 30).map((t, i) => (
                <div key={i} className="flex items-center justify-between bg-background px-2 py-1 rounded border text-xs">
                  <span className="font-mono truncate">{t.table_name}</span>
                  <span className="text-muted-foreground ml-2">{Number(t.approx_rows).toLocaleString()}</span>
                </div>
              ))}
            </div>
            {tables.length > 30 && (
              <p className="text-xs text-muted-foreground mt-2">...and {tables.length - 30} more</p>
            )}
          </div>
        );
      }

      case 'describe_table': {
        const columns = (result?.columns as Array<{ name: string; type: string; nullable: boolean }>) || [];
        return (
          <div>
            <p className="text-muted-foreground text-xs mb-2">
              Table <code className="bg-muted px-1 rounded">{String(result?.table_name ?? '')}</code> has {String(result?.column_count ?? columns.length)} columns
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
              {columns.map((col, i) => (
                <div key={i} className="flex items-center gap-2 bg-background px-2 py-1 rounded border text-xs">
                  <span className="font-mono truncate flex-1">{col.name}</span>
                  <Badge variant="outline" className="text-[10px] px-1 py-0">
                    {col.type}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        );
      }

      case 'sample_values': {
        const values = (result?.values as Array<{ value: unknown; count: number }>) || [];
        return (
          <div>
            <p className="text-muted-foreground text-xs mb-2">
              <code className="bg-muted px-1 rounded">{String(result?.table_name ?? '')}.{String(result?.column_name ?? '')}</code>
              {' — '}{String(result?.total_distinct ?? '')} distinct values (showing {String(result?.showing ?? '')})
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
              {values.slice(0, 20).map((v, i) => (
                <div key={i} className="flex items-center justify-between bg-background px-2 py-1 rounded border text-xs">
                  <span className="truncate font-medium">
                    {v.value === null ? <span className="text-muted-foreground italic">null</span> : String(v.value)}
                  </span>
                  <span className="text-muted-foreground ml-2">{Number(v.count).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        );
      }

      case 'query_odoo': {
        if (!Array.isArray(result?.columns) || !Array.isArray(result?.rows)) {
          return <p className="text-muted-foreground text-xs">No data returned</p>;
        }
        return (
          <div>
            {typeof toolCall.args?.sql === 'string' && toolCall.args.sql && (
              <div className="mb-3">
                <p className="font-medium text-muted-foreground mb-1 text-xs uppercase">SQL</p>
                <pre className="bg-background p-2 rounded text-xs overflow-auto border">{toolCall.args.sql}</pre>
              </div>
            )}
            <div className="flex items-center justify-between mb-2">
              <p className="text-muted-foreground text-xs">
                {(result.rows as unknown[][]).length} rows {result.truncated ? '(truncated)' : ''}
              </p>
              {availableColumns.length > 0 && onRefineQuery && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs gap-1.5 hover:bg-violet-50 hover:text-violet-700 hover:border-violet-200"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowColumnSelector(true);
                  }}
                >
                  <Settings2 className="h-3.5 w-3.5" />
                  Customize Columns
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 ml-1">
                    {availableColumns.length}
                  </Badge>
                </Button>
              )}
            </div>
            <div className="overflow-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b">
                    {(result.columns as string[]).map((col, i) => (
                      <th key={i} className="px-2 py-1 text-left font-medium">{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(result.rows as unknown[][]).slice(0, 10).map((row, i) => (
                    <tr key={i} className="border-b border-border/50">
                      {row.map((cell, j) => (
                        <td key={j} className="px-2 py-1 truncate max-w-[200px]">
                          {cell === null ? <span className="text-muted-foreground">null</span> : String(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {queryStructure && (
              <ColumnSelectorModal
                isOpen={showColumnSelector}
                availableColumns={availableColumns}
                queryStructure={queryStructure}
                primaryTable={primaryTable}
                onRefine={handleRefine}
                onClose={() => setShowColumnSelector(false)}
              />
            )}
          </div>
        );
      }

      case 'generate_chart': {
        return (
          <div className="text-center text-muted-foreground text-xs">
            Chart generated — see visualization above
          </div>
        );
      }

      default:
        return (
          <pre className="bg-background p-2 rounded text-xs overflow-auto border">
            {JSON.stringify(result, null, 2)}
          </pre>
        );
    }
  };

  return (
    <div className="my-2">
      <button
        onClick={() => !isLoading && setIsExpanded(!isExpanded)}
        disabled={isLoading}
        className={cn(
          "flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors w-full text-left border",
          isLoading && "bg-blue-50 text-blue-700 border-blue-200",
          !isLoading && !hasError && "bg-green-50 text-green-700 border-green-200 hover:bg-green-100",
          !isLoading && hasError && "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
        )}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : hasError ? (
          <AlertCircle className="h-4 w-4" />
        ) : (
          <CheckCircle2 className="h-4 w-4" />
        )}
        {toolDisplay.icon}
        <span className="flex-1">{toolDisplay.name}</span>
        {/* Show token usage for this tool call */}
        {!isLoading && toolCall.usage && (
          <span className="text-xs opacity-70 font-mono" title={`This iteration: ↑${toolCall.usage.inputTokens.toLocaleString()} ↓${toolCall.usage.outputTokens.toLocaleString()}`}>
            Σ ↑{toolCall.usage.cumulativeInputTokens.toLocaleString()} ↓{toolCall.usage.cumulativeOutputTokens.toLocaleString()}
          </span>
        )}
        {!isLoading && (
          <ChevronRight className={cn("h-4 w-4 transition-transform", isExpanded && "rotate-90")} />
        )}
      </button>

      {isExpanded && (
        <div className="mt-2 p-3 bg-muted/50 rounded-md border text-sm overflow-auto max-h-[400px]">
          {renderToolContent()}
        </div>
      )}
    </div>
  );
}

function CopyableTable({ children }: { children?: React.ReactNode }) {
  const [copied, setCopied] = useState(false);
  const tableRef = useRef<HTMLTableElement>(null);

  const copyAsCSV = () => {
    if (!tableRef.current) return;
    
    const rows = tableRef.current.querySelectorAll('tr');
    const csvRows: string[] = [];
    
    rows.forEach(row => {
      const cells = row.querySelectorAll('th, td');
      const rowData: string[] = [];
      cells.forEach(cell => {
        let text = cell.textContent || '';
        if (text.includes(',') || text.includes('"') || text.includes('\n')) {
          text = `"${text.replace(/"/g, '""')}"`;
        }
        rowData.push(text);
      });
      csvRows.push(rowData.join(','));
    });
    
    navigator.clipboard.writeText(csvRows.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative group my-3">
      <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
        <button
          onClick={copyAsCSV}
          className="flex items-center gap-1 px-2 py-1 text-xs bg-background border rounded-md shadow-sm hover:bg-muted transition-colors"
        >
          {copied ? (
            <>
              <Check className="h-3 w-3 text-green-500" />
              <span>Copied!</span>
            </>
          ) : (
            <>
              <Copy className="h-3 w-3" />
              <span>Copy CSV</span>
            </>
          )}
        </button>
      </div>
      <div className="overflow-auto max-h-80 border border-border rounded-lg">
        <table ref={tableRef} className="min-w-full border-collapse">
          {children}
        </table>
      </div>
    </div>
  );
}

const markdownComponents = {
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
      {children}
      <ExternalLink className="h-3 w-3" />
    </a>
  ),
  table: ({ children }: { children?: React.ReactNode }) => (
    <CopyableTable>{children}</CopyableTable>
  ),
  thead: ({ children }: { children?: React.ReactNode }) => (
    <thead className="bg-muted/50 sticky top-0">{children}</thead>
  ),
  th: ({ children }: { children?: React.ReactNode }) => (
    <th className="px-3 py-2 text-left text-xs font-semibold border-b border-border">{children}</th>
  ),
  td: ({ children }: { children?: React.ReactNode }) => (
    <td className="px-3 py-2 text-sm border-b border-border/50">{children}</td>
  ),
  tr: ({ children }: { children?: React.ReactNode }) => (
    <tr className="hover:bg-muted/30 transition-colors">{children}</tr>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="list-disc list-inside space-y-1 my-2">{children}</ul>
  ),
  ol: ({ children }: { children?: React.ReactNode }) => (
    <ol className="list-decimal list-inside space-y-1 my-2">{children}</ol>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li className="text-sm">{children}</li>
  ),
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="my-2 text-sm leading-relaxed">{children}</p>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-semibold">{children}</strong>
  ),
  code: ({ children }: { children?: React.ReactNode }) => (
    <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">{children}</code>
  ),
  pre: ({ children }: { children?: React.ReactNode }) => (
    <pre className="bg-muted p-3 rounded-lg overflow-x-auto my-2 text-xs">{children}</pre>
  ),
};

function UsageBadge({ usage, isLive = false }: { usage: TokenUsage; isLive?: boolean }) {
  const formatCost = (cost: number) => {
    if (cost < 0.001) return '<$0.001';
    if (cost < 0.01) return `$${cost.toFixed(4)}`;
    return `$${cost.toFixed(3)}`;
  };

  return (
    <div className={cn(
      "inline-flex items-center gap-2 mt-2 px-2 py-1 rounded-md text-xs",
      isLive
        ? "bg-blue-50 text-blue-600 border border-blue-200"
        : "bg-gray-100 text-gray-500"
    )}>
      {isLive && (
        <span className="flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-blue-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500"></span>
        </span>
      )}
      <span title="Input tokens">↑ {usage.inputTokens.toLocaleString()}</span>
      <span title="Output tokens">↓ {usage.outputTokens.toLocaleString()}</span>
      <span className={cn("font-medium", isLive ? "text-blue-700" : "text-gray-700")} title={`Input: $${usage.inputCost.toFixed(6)} | Output: $${usage.outputCost.toFixed(6)}`}>
        {formatCost(usage.totalCost)}
      </span>
      {isLive && <span className="text-blue-500">updating...</span>}
    </div>
  );
}

interface AssistantMessageProps {
  turn?: AssistantTurn;
  content: string;
  onRefineQuery?: (
    baseQuery: { primaryTable: string; joins?: string; conditions?: string; orderBy?: string; limit?: number },
    selectedColumns: string[]
  ) => void;
}

function AssistantMessage({ turn, content, onRefineQuery }: AssistantMessageProps) {
  const { text, charts } = parseChartsFromMarkdown(content || '');
  
  if (!turn) {
    return (
      <div className="max-w-none">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
          {text}
        </ReactMarkdown>
        {charts.map((chart, i) => (
          <ChartRenderer key={i} chart={chart} />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {turn.thinking && (
        <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 text-amber-800 rounded-md border border-amber-200">
          <Lightbulb className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <p className="text-sm">{turn.thinking}</p>
        </div>
      )}
      
      {turn.toolCalls.map((toolCall) => (
        <ToolCallCard key={toolCall.id} toolCall={toolCall} onRefineQuery={onRefineQuery} />
      ))}
      
      {turn.isLoading && (
        <div className="flex items-center gap-3 text-muted-foreground py-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">
            {!turn.thinking && turn.toolCalls.length === 0 && 'Analyzing your question...'}
            {turn.toolCalls.length > 0 && turn.toolCalls.every(tc => tc.status === 'completed') && !content && 'Generating response...'}
            {turn.toolCalls.some(tc => tc.status === 'running') && 'Waiting for results...'}
          </span>
        </div>
      )}
      
      {content && (
        <div className="max-w-none">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {text}
          </ReactMarkdown>
          {charts.map((chart, i) => (
            <ChartRenderer key={i} chart={chart} />
          ))}
        </div>
      )}

      {/* Show usage/cost badge - real-time during loading, final after complete */}
      {turn.usage && (
        <UsageBadge
          usage={turn.usage}
          isLive={turn.isLoading}
        />
      )}
    </div>
  );
}

interface ChatMessageProps {
  message: Message;
  onRefineQuery?: (
    baseQuery: { primaryTable: string; joins?: string; conditions?: string; orderBy?: string; limit?: number },
    selectedColumns: string[]
  ) => void;
}

function ChatMessage({ message, onRefineQuery }: ChatMessageProps) {
  const isUser = message.role === 'user';

  return (
    <div className={cn("flex gap-3 py-4", isUser && "flex-row-reverse")}>
      <div className={cn(
        "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white",
        isUser ? "bg-primary" : "bg-gradient-to-br from-violet-500 to-purple-600"
      )}>
        {isUser ? (
          <span className="text-xs font-medium">You</span>
        ) : (
          <Sparkles className="h-4 w-4" />
        )}
      </div>
      <div className={cn("flex-1 max-w-[85%]", isUser && "text-right")}>
        {isUser ? (
          <div className="inline-block text-left">
            {/* Display images if present */}
            {message.images && message.images.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {message.images.map((img, idx) => (
                  <img
                    key={idx}
                    src={img.previewUrl || `data:${img.mediaType};base64,${img.data}`}
                    alt={`Attachment ${idx + 1}`}
                    className="max-w-[200px] max-h-[200px] rounded-lg border object-cover"
                  />
                ))}
              </div>
            )}
            {/* Display text files if present */}
            {message.textFiles && message.textFiles.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {message.textFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-lg border">
                    <FileText className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-foreground">{file.filename}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="bg-muted rounded-lg rounded-tr-sm px-4 py-2">
              <p className="text-sm">{message.content}</p>
            </div>
          </div>
        ) : (
          <AssistantMessage turn={message.turn} content={message.content} onRefineQuery={onRefineQuery} />
        )}
      </div>
    </div>
  );
}

function AgentCard({ agent, onToggle, onDelete }: {
  agent: DisplayAgent;
  onToggle: () => void;
  onDelete: () => void;
}) {
  return (
    <Card className={cn(!agent.enabled && "opacity-60")}>
      <CardHeader className="p-4 pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={cn(
              "w-9 h-9 rounded-lg flex items-center justify-center",
              agent.enabled ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
            )}>
              <Bot className="h-4 w-4" />
            </div>
            <div>
              <CardTitle className="text-sm">{agent.name}</CardTitle>
              <CardDescription className="text-xs">{agent.description}</CardDescription>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onToggle}>
                {agent.enabled ? <Pause className="h-4 w-4 mr-2" /> : <Play className="h-4 w-4 mr-2" />}
                {agent.enabled ? 'Disable' : 'Enable'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onDelete} className="text-red-600">
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-2">
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            <span>{agent.cronExpression}</span>
          </div>
          <ArrowRight className="h-3 w-3" />
          <div className="flex items-center gap-1">
            {agent.actionType === 'email' && <Mail className="h-3 w-3" />}
            {agent.actionType === 'webhook' && <Webhook className="h-3 w-3" />}
            <span className="capitalize">{agent.actionType}</span>
          </div>
          <div className="flex-1" />
          {agent.lastRunStatus && (
            <div className="flex items-center gap-1">
              {agent.lastRunStatus === 'success' && <CheckCircle2 className="h-3 w-3 text-green-500" />}
              {agent.lastRunStatus === 'failed' && <AlertCircle className="h-3 w-3 text-red-500" />}
              {agent.lastRunStatus === 'skipped' && <Badge variant="secondary" className="text-[10px] h-4">Skipped</Badge>}
            </div>
          )}
          <Badge variant={agent.enabled ? "default" : "secondary"} className="text-[10px] h-5">
            {agent.enabled ? 'Active' : 'Paused'}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
}

export function DoployAiPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [activeTab, setActiveTab] = useState<'chat' | 'agents'>('chat');
  const [inputValue, setInputValue] = useState('');
  const [selectedBuildId, setSelectedBuildId] = useState<number | null>(null);
  const [selectedOdooUserId, setSelectedOdooUserId] = useState<string | null>(null);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [showBuilder, setShowBuilder] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [editingChatId, setEditingChatId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [loadingChatId, setLoadingChatId] = useState<string | null>(null);
  const [pendingImages, setPendingImages] = useState<ImageAttachment[]>([]);
  const [pendingTextFiles, setPendingTextFiles] = useState<TextFileAttachment[]>([]);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: branchesData } = useQuery({
    queryKey: ['branches', projectId],
    queryFn: () => getBranches(projectId!),
    enabled: !!projectId,
  });

  const deployments: Deployment[] = (branchesData?.branches_with_builds || [])
    .filter(b => b.latest_build?.id && (b.branch_type === 'production' || b.branch_type === 'staging'))
    .map(b => ({
      buildId: b.latest_build!.id,
      branchName: b.name,
      branchType: b.branch_type,
      fqdn: b.latest_build!.fqdn,
    }));

  useEffect(() => {
    if (deployments.length > 0 && !selectedBuildId) {
      const production = deployments.find(d => d.branchType === 'production');
      setSelectedBuildId(production?.buildId || deployments[0].buildId);
    }
  }, [deployments, selectedBuildId]);

  const selectedDeployment = deployments.find(d => d.buildId === selectedBuildId);

  const { data: odooUsersData, isLoading: isLoadingOdooUsers } = useQuery({
    queryKey: ['odooUsers', projectId, selectedBuildId],
    queryFn: () => getOdooUsers(projectId!, selectedBuildId!),
    enabled: !!projectId && !!selectedBuildId,
  });

  const odooUsers = odooUsersData?.users || [];

  // Knowledge base status and indexing
  const { data: knowledgeStatus, isLoading: isLoadingKnowledge, refetch: refetchKnowledge } = useQuery({
    queryKey: ['knowledgeStatus', projectId, selectedBuildId],
    queryFn: () => getKnowledgeStatus(projectId!, selectedBuildId || undefined),
    enabled: !!projectId,
  });

  const [isIndexing, setIsIndexing] = useState(false);
  const [showKnowledgeViewer, setShowKnowledgeViewer] = useState(false);
  const [includeCodeInIndex, setIncludeCodeInIndex] = useState(false);

  const handleIndexKnowledge = async () => {
    if (!projectId || !selectedBuildId) return;
    setIsIndexing(true);
    try {
      await indexKnowledgeBase(projectId, selectedBuildId, {
        force: knowledgeStatus?.indexed || false,
        includeCode: includeCodeInIndex,
      });
      refetchKnowledge();
      toast.success('Knowledge base indexed successfully');
    } catch (error) {
      console.error('Failed to index knowledge base:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to index knowledge base');
    } finally {
      setIsIndexing(false);
    }
  };

  // Agents query and mutations
  const queryClient = useQueryClient();

  const { data: agentsData, isLoading: _isLoadingAgents } = useQuery({
    queryKey: ['agents', projectId],
    queryFn: () => getAgents(projectId!),
    enabled: !!projectId,
  });

  const agents: DisplayAgent[] = (agentsData?.agents || []).map(mapApiAgentToDisplay);
  const activeAgents = agents.filter(a => a.enabled).length;

  const updateAgentMutation = useMutation({
    mutationFn: ({ agentId, updates }: { agentId: string; updates: { enabled?: boolean } }) =>
      updateAgent(agentId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents', projectId] });
    },
  });

  const deleteAgentMutation = useMutation({
    mutationFn: (agentId: string) => deleteAgentApi(agentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents', projectId] });
    },
  });

  const createAgentMutation = useMutation({
    mutationFn: (config: AgentConfig) => {
      if (!projectId || !selectedBuildId) {
        throw new Error('Project or build not selected');
      }
      return createAgent(projectId, {
        name: config.name,
        description: config.description,
        buildId: selectedBuildId,
        trigger: {
          type: config.trigger.type,
          cronExpression: config.trigger.cronExpression,
        },
        condition: {
          type: config.condition.type,
          sql: config.condition.sql || '',
        },
        action: {
          type: config.action.type,
          email: config.action.email,
          webhook: config.action.webhook,
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents', projectId] });
      setShowBuilder(false);
    },
  });

  const {
    messages,
    sendMessage,
    refineQuery,
    clearMessages,
    loadMessages,
    isConnected,
    isLoading,
    reconnect,
    schema,
    schemaStatus,
    schemaError,
    conversationId,
  } = useOdooAgent(projectId!, selectedBuildId || 0, selectedDeployment?.fqdn, {
    odooUserId: selectedOdooUserId ? parseInt(selectedOdooUserId, 10) : undefined,
  });

  const [showSchema, setShowSchema] = useState(false);
  const [schemaSearch, setSchemaSearch] = useState('');
  const [expandedModel, setExpandedModel] = useState<string | null>(null);
  const [showRawSchema, setShowRawSchema] = useState<boolean | null>(null); // null=formatted, false=prompt, true=json

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 150) + 'px';
    }
  };

  // Handle file selection (images and text files)
  const handleFileUpload = useCallback((files: FileList | null) => {
    if (!files) return;

    const imageTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
    const textTypes = ['text/csv', 'text/plain', 'application/json', 'text/tab-separated-values'];
    const textExtensions = ['.csv', '.txt', '.json', '.tsv', '.md', '.xml', '.yaml', '.yml'];
    const maxImageSize = 5 * 1024 * 1024; // 5MB for images
    const maxTextSize = 100 * 1024; // 100KB for text files

    Array.from(files).forEach(file => {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      const isImage = imageTypes.includes(file.type);
      const isTextFile = textTypes.includes(file.type) || textExtensions.includes(ext);

      if (isImage) {
        if (file.size > maxImageSize) {
          console.warn('Image too large:', file.size);
          return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
          const dataUrl = e.target?.result as string;
          const base64Data = dataUrl.split(',')[1];
          const attachment: ImageAttachment = {
            type: 'image',
            mediaType: file.type as ImageAttachment['mediaType'],
            data: base64Data,
            previewUrl: dataUrl,
          };
          setPendingImages(prev => [...prev, attachment]);
        };
        reader.readAsDataURL(file);
      } else if (isTextFile) {
        if (file.size > maxTextSize) {
          console.warn('Text file too large (max 100KB):', file.size);
          return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target?.result as string;
          const attachment: TextFileAttachment = {
            type: 'text_file',
            filename: file.name,
            mimeType: file.type || 'text/plain',
            content,
          };
          setPendingTextFiles(prev => [...prev, attachment]);
        };
        reader.readAsText(file);
      } else {
        console.warn('Unsupported file type:', file.type, file.name);
      }
    });
  }, []);

  const removeImage = useCallback((index: number) => {
    setPendingImages(prev => prev.filter((_, i) => i !== index));
  }, []);

  const removeTextFile = useCallback((index: number) => {
    setPendingTextFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Handle paste events for images
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of Array.from(items)) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          const reader = new FileReader();
          reader.onload = (event) => {
            const dataUrl = event.target?.result as string;
            const base64Data = dataUrl.split(',')[1];

            const attachment: ImageAttachment = {
              type: 'image',
              mediaType: item.type as ImageAttachment['mediaType'],
              data: base64Data,
              previewUrl: dataUrl,
            };
            setPendingImages(prev => [...prev, attachment]);
          };
          reader.readAsDataURL(file);
        }
        break;
      }
    }
  }, []);

  const handleSend = useCallback(() => {
    const hasAttachments = pendingImages.length > 0 || pendingTextFiles.length > 0;
    if ((!inputValue.trim() && !hasAttachments) || !isConnected || isLoading) return;

    // Send message with attachments
    const defaultMsg = pendingImages.length > 0 ? 'Please analyze this image.' : 'Please analyze this file.';
    sendMessage(
      inputValue.trim() || defaultMsg,
      pendingImages.length > 0 ? pendingImages : undefined,
      pendingTextFiles.length > 0 ? pendingTextFiles : undefined
    );
    setInputValue('');
    setPendingImages([]);
    setPendingTextFiles([]);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [inputValue, pendingImages, pendingTextFiles, isConnected, isLoading, sendMessage]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDeploymentChange = (value: string) => {
    const newBuildId = parseInt(value, 10);
    setSelectedBuildId(newBuildId);
    setSelectedOdooUserId(null);
    clearMessages();
    reconnect();
  };

  const handleOdooUserChange = (value: string) => {
    setSelectedOdooUserId(value === 'all' ? null : value);
    clearMessages();
    reconnect();
  };

  const handleNewChat = () => {
    clearMessages();
    setActiveChatId(null);
  };

  const handleLoadConversation = async (chatId: string) => {
    setActiveChatId(chatId);
    setLoadingChatId(chatId);
    try {
      const response = await getAIConversationMessages(chatId);
      const loadedMessages: Message[] = response.messages
        .filter((m: AIMessage) => m.role === 'user' || m.role === 'assistant')
        .map((m: AIMessage) => ({
          id: m.id,
          role: m.role as 'user' | 'assistant',
          content: m.content,
          timestamp: new Date(m.created_at),
        }));
      loadMessages(loadedMessages, chatId);
    } catch (error) {
      console.error('Failed to load conversation:', error);
    } finally {
      setLoadingChatId(null);
    }
  };

  const handleDeleteConversation = async (id: string) => {
    try {
      await deleteAIConversation(id);
      if (activeChatId === id) {
        setActiveChatId(null);
        clearMessages();
      }
      refetchConversations();
    } catch (error) {
      console.error('Failed to delete conversation:', error);
    }
  };

  const handleStartRename = (id: string, currentTitle: string) => {
    setEditingChatId(id);
    setEditingTitle(currentTitle);
  };

  const handleRenameConversation = async (id: string) => {
    if (!editingTitle.trim()) {
      setEditingChatId(null);
      return;
    }
    try {
      await updateAIConversationTitle(id, editingTitle.trim());
      refetchConversations();
    } catch (error) {
      console.error('Failed to rename conversation:', error);
    }
    setEditingChatId(null);
  };

  const handleToggleAgent = (id: string) => {
    const agent = agents.find(a => a.id === id);
    if (agent) {
      updateAgentMutation.mutate({ agentId: id, updates: { enabled: !agent.enabled } });
    }
  };

  const handleDeleteAgent = (id: string) => {
    deleteAgentMutation.mutate(id);
  };

  const { 
    data: conversationsData, 
    refetch: refetchConversations 
  } = useQuery({
    queryKey: ['aiConversations', projectId],
    queryFn: () => getAIConversations(projectId!),
    enabled: !!projectId,
    refetchInterval: 30000,
  });

  useEffect(() => {
    if (conversationId) {
      refetchConversations();
    }
  }, [conversationId, refetchConversations]);

  const chatHistory: ChatHistory[] = (conversationsData?.conversations || []).map((c: AIConversation) => ({
    id: c.id,
    title: c.title || 'New conversation',
    preview: `${c.message_count} messages`,
    timestamp: c.last_message_at ? new Date(c.last_message_at) : new Date(c.created_at),
  }));

  const filteredHistory = chatHistory.filter(h => 
    h.title.toLowerCase().includes(historySearch.toLowerCase()) ||
    h.preview.toLowerCase().includes(historySearch.toLowerCase())
  );

  return (
    <DashboardLayout noScroll>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-full min-h-0">
        {/* Sidebar */}
        <div className="lg:col-span-1 flex flex-col min-h-0">
          <Card className="flex-1 flex flex-col min-h-0">
            <CardHeader className="pb-3 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-muted-foreground/50" />
                  <CardTitle className="text-base">doploy AI</CardTitle>
                </div>
                <Badge variant={isConnected ? "default" : "secondary"} className={cn(
                  "text-[10px]",
                  isConnected && "bg-green-100 text-green-700 hover:bg-green-100"
                )}>
                  {isConnected ? 'Live' : 'Offline'}
                </Badge>
              </div>
              {deployments.length > 0 && (
                <Select value={String(selectedBuildId)} onValueChange={handleDeploymentChange}>
                  <SelectTrigger className="h-8 text-xs mt-3">
                    <SelectValue placeholder="Select deployment" />
                  </SelectTrigger>
                  <SelectContent>
                    {deployments.map((deployment) => (
                      <SelectItem key={deployment.buildId} value={String(deployment.buildId)}>
                        <div className="flex items-center gap-2">
                          <span className={cn(
                            "w-2 h-2 rounded-full",
                            deployment.branchType === 'production' && "bg-green-500",
                            deployment.branchType === 'staging' && "bg-blue-500"
                          )} />
                          <span className="truncate">{deployment.branchName}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {selectedBuildId && (
                <Select
                  value={selectedOdooUserId || 'all'}
                  onValueChange={handleOdooUserChange}
                  disabled={isLoadingOdooUsers}
                >
                  <SelectTrigger className="h-8 text-xs mt-2">
                    <SelectValue placeholder="Query as user..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      <span className="text-muted-foreground">All data (admin)</span>
                    </SelectItem>
                    {odooUsers.map((odooUser) => (
                      <SelectItem key={odooUser.id} value={odooUser.id}>
                        {odooUser.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {selectedBuildId && (
                <div className="mt-3 pt-3 border-t">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Database className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Knowledge Base</span>
                    </div>
                    {knowledgeStatus?.indexed ? (
                      <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        Indexed
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                        Not indexed
                      </Badge>
                    )}
                  </div>
                  {knowledgeStatus?.indexed && knowledgeStatus.stats && (
                    <button
                      onClick={() => setShowKnowledgeViewer(true)}
                      className="text-[10px] text-muted-foreground mt-1 hover:text-primary hover:underline cursor-pointer text-left"
                    >
                      {knowledgeStatus.stats.modelsIndexed} models, {knowledgeStatus.stats.fieldsIndexed} fields
                      {knowledgeStatus.stats.codeChunksIndexed ? `, ${knowledgeStatus.stats.codeChunksIndexed} code` : ''}
                      <span className="ml-1 text-primary">(view)</span>
                    </button>
                  )}

                  {/* Include code toggle */}
                  <div className="flex items-center justify-between mt-2 py-1.5 px-2 bg-muted/50 rounded-md">
                    <label htmlFor="include-code" className="text-[10px] text-muted-foreground cursor-pointer">
                      Include custom code
                    </label>
                    <Switch
                      id="include-code"
                      checked={includeCodeInIndex}
                      onCheckedChange={setIncludeCodeInIndex}
                      className="scale-75"
                    />
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-2 h-7 text-xs"
                    onClick={handleIndexKnowledge}
                    disabled={isIndexing || isLoadingKnowledge}
                  >
                    {isIndexing ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                        Indexing{includeCodeInIndex ? ' (with code)' : ''}...
                      </>
                    ) : knowledgeStatus?.indexed ? (
                      <>
                        <Zap className="h-3 w-3 mr-1.5" />
                        Re-index{includeCodeInIndex ? ' + Code' : ''}
                      </>
                    ) : (
                      <>
                        <Zap className="h-3 w-3 mr-1.5" />
                        Index for RAG{includeCodeInIndex ? ' + Code' : ''}
                      </>
                    )}
                  </Button>
                  {!knowledgeStatus?.indexed && (
                    <p className="text-[10px] text-muted-foreground mt-1.5 text-center">
                      Reduces token usage by ~80%
                    </p>
                  )}
                </div>
              )}
            </CardHeader>
            <CardContent className="pt-0 flex-1 flex flex-col min-h-0">
              <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'chat' | 'agents')} className="flex-1 flex flex-col min-h-0">
                <TabsList className="grid w-full grid-cols-2 h-9">
                  <TabsTrigger value="chat" className="text-xs gap-1.5">
                    <MessageSquare className="h-3 w-3" />
                    Chat
                  </TabsTrigger>
                  <TabsTrigger value="agents" className="text-xs gap-1.5">
                    <Bot className="h-3 w-3" />
                    Agents
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="chat" className="mt-4 space-y-3 flex-1 flex flex-col min-h-0">
                  <Button onClick={handleNewChat} variant="outline" className="w-full justify-start gap-2 h-9 text-xs">
                    <Plus className="h-3 w-3" />
                    New chat
                  </Button>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                    <Input
                      value={historySearch}
                      onChange={(e) => setHistorySearch(e.target.value)}
                      placeholder="Search history..."
                      className="h-8 text-xs pl-7"
                    />
                  </div>
                  <Separator />
                  <div>
                    <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">Recent</span>
                  </div>
                  <div className="space-y-1 flex-1 overflow-y-auto">
                    {filteredHistory.map((chat) => (
                      <div
                        key={chat.id}
                        onClick={() => editingChatId !== chat.id && handleLoadConversation(chat.id)}
                        className={cn(
                          "w-full text-left px-3 py-2 rounded-md text-sm transition-colors cursor-pointer group flex items-center justify-between",
                          activeChatId === chat.id
                            ? "bg-primary/10 text-primary"
                            : "hover:bg-muted"
                        )}
                      >
                        <div className="min-w-0 flex-1">
                          {editingChatId === chat.id ? (
                            <Input
                              value={editingTitle}
                              onChange={(e) => setEditingTitle(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  handleRenameConversation(chat.id);
                                } else if (e.key === 'Escape') {
                                  setEditingChatId(null);
                                }
                              }}
                              onBlur={() => handleRenameConversation(chat.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="h-6 text-xs py-0"
                              autoFocus
                            />
                          ) : (
                            <>
                              <div className="flex items-center gap-1.5">
                                {loadingChatId === chat.id && (
                                  <Loader2 className="h-3 w-3 animate-spin text-muted-foreground flex-shrink-0" />
                                )}
                                <p className="font-medium truncate text-xs">{chat.title}</p>
                              </div>
                              <p className="text-[10px] text-muted-foreground truncate">{chat.preview}</p>
                            </>
                          )}
                        </div>
                        {editingChatId !== chat.id && (
                          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStartRename(chat.id, chat.title);
                              }}
                              className="p-1 hover:bg-muted rounded"
                            >
                              <Pencil className="h-3 w-3 text-muted-foreground" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteConversation(chat.id);
                              }}
                              className="p-1 hover:bg-red-100 rounded"
                            >
                              <Trash2 className="h-3 w-3 text-red-500" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="agents" className="mt-4 space-y-3 flex-1 flex flex-col min-h-0">
                  <Button onClick={() => setShowBuilder(true)} className="w-full justify-start gap-2 h-9 text-xs">
                    <Plus className="h-3 w-3" />
                    Create agent
                  </Button>
                  <Separator />
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-muted/50 rounded-md p-2 border">
                      <p className="text-lg font-semibold">{agents.length}</p>
                      <p className="text-[10px] text-muted-foreground">Total</p>
                    </div>
                    <div className="bg-muted/50 rounded-md p-2 border">
                      <p className="text-lg font-semibold text-green-600">{activeAgents}</p>
                      <p className="text-[10px] text-muted-foreground">Active</p>
                    </div>
                  </div>
                  <div className="space-y-2 flex-1 overflow-y-auto">
                    {agents.map(agent => (
                      <div
                        key={agent.id}
                        className={cn(
                          "px-3 py-2 rounded-md border text-xs transition-colors cursor-pointer hover:border-primary/40",
                          !agent.enabled && "opacity-50"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <Bot className="h-3 w-3" />
                          <span className="font-medium truncate">{agent.name}</span>
                          {agent.lastRunStatus === 'success' && <CheckCircle2 className="h-3 w-3 text-green-500 ml-auto" />}
                          {agent.lastRunStatus === 'failed' && <AlertCircle className="h-3 w-3 text-red-500 ml-auto" />}
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-1 truncate">{agent.description}</p>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3 flex flex-col min-h-0">
          {activeTab === 'chat' ? (
            <Card className="flex-1 flex flex-col min-h-0">
              <CardContent className="p-0 flex-1 flex flex-col min-h-0">
                {/* Schema Status Bar */}
                <div className="border-b px-4 py-2 flex items-center justify-between bg-muted/30">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-muted-foreground">Schema:</span>
                    {schemaStatus === 'loading' && (
                      <span className="flex items-center gap-1 text-yellow-600">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Loading...
                      </span>
                    )}
                    {schemaStatus === 'loaded' && schema && (
                      <span className="text-green-600">
                        {schema.model_count} models loaded
                      </span>
                    )}
                    {schemaStatus === 'error' && (
                      <span className="text-red-600">Failed to load</span>
                    )}
                    {!schemaStatus && (
                      <span className="text-muted-foreground">Not loaded</span>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs"
                    onClick={() => setShowSchema(!showSchema)}
                  >
                    {showSchema ? 'Hide Schema' : 'View Schema'}
                  </Button>
                </div>
                
                {/* Schema Viewer */}
                {showSchema && (
                  <div className="border-b bg-muted/20 max-h-[500px] flex flex-col">
                    {schemaError && (
                      <div className="bg-red-50 border border-red-200 rounded p-3 m-4 mb-0">
                        <p className="text-sm font-medium text-red-800 mb-2">Schema Load Error:</p>
                        <pre className="text-xs text-red-700 whitespace-pre-wrap overflow-auto max-h-[200px]">
                          {schemaError}
                        </pre>
                      </div>
                    )}
                    {schema ? (
                      <div className="flex flex-col h-full">
                        <div className="p-4 pb-2 flex items-center gap-4 flex-wrap">
                          <p className="text-sm font-medium">
                            {schema.model_count} models ({schema.table_count} tables)
                          </p>
                          <div className="flex gap-2 text-xs">
                            <span className={`px-2 py-0.5 rounded ${showRawSchema === false ? 'bg-green-100 text-green-800' : 'bg-muted text-muted-foreground'}`}>
                              Prompt: ~{(schema.prompt_tokens || 0).toLocaleString()} tokens
                            </span>
                            <span className={`px-2 py-0.5 rounded ${showRawSchema === true ? 'bg-orange-100 text-orange-800' : 'bg-muted text-muted-foreground'}`}>
                              JSON: ~{(schema.json_tokens || Math.round(JSON.stringify(schema.full_schema).length / 4)).toLocaleString()} tokens
                            </span>
                            {schema.json_tokens && schema.prompt_tokens && (
                              <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-800">
                                {Math.round((1 - schema.prompt_tokens / schema.json_tokens) * 100)}% smaller
                              </span>
                            )}
                          </div>
                          <div className="relative flex-1 max-w-xs">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                            <Input
                              value={schemaSearch}
                              onChange={(e) => setSchemaSearch(e.target.value)}
                              placeholder="Search models/tables..."
                              className="h-7 text-xs pl-7"
                              disabled={showRawSchema !== null}
                            />
                          </div>
                          <div className="flex gap-1">
                            <Button
                              variant={showRawSchema === null ? "default" : "outline"}
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => setShowRawSchema(null)}
                            >
                              Formatted
                            </Button>
                            <Button
                              variant={showRawSchema === false ? "default" : "outline"}
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => setShowRawSchema(false)}
                            >
                              Prompt
                            </Button>
                            <Button
                              variant={showRawSchema === true ? "default" : "outline"}
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => setShowRawSchema(true)}
                            >
                              JSON
                            </Button>
                          </div>
                        </div>
                        <div className="flex-1 overflow-auto px-4 pb-4">
                          {showRawSchema === true ? (
                            <pre className="bg-muted p-4 rounded text-xs font-mono whitespace-pre-wrap overflow-auto max-h-[400px]">
                              {JSON.stringify(schema.full_schema, null, 2)}
                            </pre>
                          ) : showRawSchema === false ? (
                            <pre className="bg-muted p-4 rounded text-xs font-mono whitespace-pre-wrap overflow-auto max-h-[400px]">
                              {schema.prompt_format || 'Prompt format not available - redeploy cloudflare worker'}
                            </pre>
                          ) : (
                            <div className="grid gap-2">
                              {schema.models_preview
                                ?.filter(m =>
                                  !schemaSearch ||
                                  m.model?.toLowerCase().includes(schemaSearch.toLowerCase()) ||
                                  m.table?.toLowerCase().includes(schemaSearch.toLowerCase()) ||
                                  (typeof m.name === 'string' && m.name.toLowerCase().includes(schemaSearch.toLowerCase()))
                                )
                                .map((model) => {
                                  const fullModel = schema.full_schema?.[model.model] as OdooModelFull | undefined;
                                  const isExpanded = expandedModel === model.model;
                                  return (
                                    <div key={model.model} className="bg-background border rounded text-xs">
                                      <button
                                        onClick={() => setExpandedModel(isExpanded ? null : model.model)}
                                        className="w-full p-2 text-left flex items-center justify-between hover:bg-muted/50"
                                      >
                                        <div className="flex items-center gap-2">
                                          <ChevronRight className={cn("h-3 w-3 transition-transform", isExpanded && "rotate-90")} />
                                          <span className="font-medium">{model.model}</span>
                                          <span className="text-muted-foreground">({model.table})</span>
                                        </div>
                                        <span className="text-muted-foreground">{model.field_count} fields</span>
                                      </button>
                                      {isExpanded && fullModel && (
                                        <div className="border-t p-2 bg-muted/30">
                                          <p className="text-muted-foreground mb-2">
                                            {typeof fullModel.name === 'string'
                                              ? fullModel.name
                                              : (fullModel.name && typeof fullModel.name === 'object'
                                                  ? Object.values(fullModel.name)[0] || model.model
                                                  : model.model)}
                                          </p>
                                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1">
                                            {Object.entries(fullModel.fields || {}).map(([name, field]) => {
                                              const fieldLabel = typeof field.label === 'string'
                                                ? field.label
                                                : (field.label && typeof field.label === 'object'
                                                    ? Object.values(field.label)[0] || name
                                                    : name);
                                              return (
                                                <div
                                                  key={name}
                                                  className={cn(
                                                    "px-1.5 py-0.5 rounded text-[10px]",
                                                    field.relation ? "bg-blue-100 text-blue-800" : "bg-muted"
                                                  )}
                                                  title={`${fieldLabel} (${field.type})${field.required ? ' [required]' : ''}${field.relation ? ` → ${field.relation}` : ''}`}
                                                >
                                                  <span className="font-mono">{name}</span>
                                                  <span className="text-muted-foreground ml-1">{field.type}</span>
                                                  {field.relation && <span className="ml-1">→</span>}
                                                </div>
                                              );
                                            })}
                                          </div>
                                          {model.relations?.length > 0 && (
                                            <div className="mt-2 pt-2 border-t">
                                              <p className="text-muted-foreground mb-1">Relations:</p>
                                              <div className="flex flex-wrap gap-1">
                                                {model.relations.map(r => (
                                                  <span key={r.field} className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded text-[10px]">
                                                    {r.field} → {r.target}
                                                  </span>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                            </div>
                          )}
                        </div>
                      </div>
                    ) : schemaStatus === 'loading' ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No schema loaded yet. Send a message to trigger schema loading.
                      </p>
                    )}
                  </div>
                )}

                {/* Chat Messages */}
                <div className="flex-1 overflow-y-auto p-6 min-h-0" ref={chatContainerRef}>
                  {loadingChatId ? (
                    <div className="flex h-full flex-col items-center justify-center">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-3" />
                      <p className="text-sm text-muted-foreground">Loading conversation...</p>
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex h-full flex-col items-center justify-center p-8 text-center">
                      <Sparkles className="mb-4 h-12 w-12 text-muted-foreground/50" />
                      <h3 className="mb-2 text-lg font-medium">How can I help?</h3>
                      <p className="mb-6 text-sm text-muted-foreground">
                        Ask questions about your Odoo data in natural language.
                      </p>
                      <div className="space-y-2 text-sm">
                        <p className="text-muted-foreground">Try asking:</p>
                        <div className="flex flex-wrap justify-center gap-2">
                          {[
                            'What are my top 10 customers?',
                            'Show me overdue invoices',
                            'List recent sales orders',
                          ].map((suggestion) => (
                            <button
                              key={suggestion}
                              className="rounded-full border bg-muted/50 px-3 py-1.5 text-xs transition-colors hover:bg-muted disabled:opacity-50"
                              onClick={() => isConnected && !isLoading && sendMessage(suggestion)}
                              disabled={!isConnected || isLoading}
                            >
                              {suggestion}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="max-w-4xl mx-auto">
                      {messages.map((message) => (
                        <ChatMessage key={message.id} message={message} onRefineQuery={refineQuery} />
                      ))}
                    </div>
                  )}
                </div>

                {/* Input */}
                <div className="p-4 border-t flex-shrink-0">
                  <div className="max-w-4xl mx-auto">
                    {/* Hidden file input for file upload */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={(e) => {
                        handleFileUpload(e.target.files);
                        e.target.value = ''; // Reset so same file can be selected again
                      }}
                      accept="image/png,image/jpeg,image/gif,image/webp,.csv,.txt,.json,.tsv,.md,.xml,.yaml,.yml"
                      multiple
                      className="hidden"
                    />

                    {/* Attachments preview section */}
                    {(pendingImages.length > 0 || pendingTextFiles.length > 0) && (
                      <div className="flex flex-wrap gap-2 mb-2 p-2 bg-muted/50 rounded-t-lg border border-b-0">
                        {/* Image previews */}
                        {pendingImages.map((img, idx) => (
                          <div key={`img-${idx}`} className="relative group">
                            <img
                              src={img.previewUrl}
                              alt={`Upload ${idx + 1}`}
                              className="h-14 w-14 object-cover rounded-lg border"
                            />
                            <button
                              onClick={() => removeImage(idx)}
                              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                        {/* Text file previews */}
                        {pendingTextFiles.map((file, idx) => (
                          <div key={`file-${idx}`} className="relative group flex items-center gap-2 px-3 py-2 bg-background rounded-lg border">
                            <FileText className="h-4 w-4 text-muted-foreground" />
                            <span className="text-xs text-foreground max-w-[120px] truncate">{file.filename}</span>
                            <button
                              onClick={() => removeTextFile(idx)}
                              className="text-muted-foreground hover:text-red-500"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className={cn(
                      "flex items-end gap-2 bg-muted/50 border p-2",
                      (pendingImages.length > 0 || pendingTextFiles.length > 0) ? "rounded-b-lg" : "rounded-lg"
                    )}>
                      <Button
                        onClick={() => fileInputRef.current?.click()}
                        disabled={!isConnected}
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 flex-shrink-0 text-muted-foreground hover:text-foreground"
                        title="Attach file (images, CSV, JSON, etc.)"
                      >
                        <Paperclip className="h-4 w-4" />
                      </Button>
                      <Textarea
                        ref={textareaRef}
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        placeholder={isConnected ? "Ask about your Odoo data... (paste or attach files)" : "Connecting..."}
                        disabled={!isConnected}
                        className="flex-1 resize-none border-0 bg-transparent focus-visible:ring-0 min-h-[40px] max-h-[150px] py-2 text-sm"
                        rows={1}
                      />
                      <Button
                        onClick={handleSend}
                        disabled={(!inputValue.trim() && pendingImages.length === 0 && pendingTextFiles.length === 0) || !isConnected || isLoading}
                        size="icon"
                        className="h-9 w-9 flex-shrink-0"
                      >
                        {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      </Button>
                    </div>
                    <p className="text-[10px] text-center text-muted-foreground mt-2">
                      AI can make mistakes. Verify important data.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Bot className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-2xl font-semibold">{agents.length}</p>
                        <p className="text-xs text-muted-foreground">Total Agents</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                        <Zap className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-semibold">{activeAgents}</p>
                        <p className="text-xs text-muted-foreground">Active</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <History className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-semibold">{agents.filter(a => a.lastRunAt).length}</p>
                        <p className="text-xs text-muted-foreground">With History</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Agent List */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle>Your Agents</CardTitle>
                    <Button onClick={() => setShowBuilder(true)} size="sm" className="gap-2">
                      <Plus className="h-4 w-4" />
                      Create Agent
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {agents.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12">
                      <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center mb-3">
                        <Bot className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">No agents yet</p>
                      <Button onClick={() => setShowBuilder(true)} variant="outline" size="sm">
                        Create your first agent
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {agents.map(agent => (
                        <AgentCard
                          key={agent.id}
                          agent={agent}
                          onToggle={() => handleToggleAgent(agent.id)}
                          onDelete={() => handleDeleteAgent(agent.id)}
                        />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </div>

      <Dialog open={showBuilder} onOpenChange={setShowBuilder}>
        <DialogContent className="sm:max-w-5xl h-[85vh] flex flex-col p-0 gap-0" aria-describedby={undefined}>
          <VisuallyHidden.Root>
            <DialogTitle>Create AI Agent</DialogTitle>
          </VisuallyHidden.Root>
          <AgentBuilder
            projectId={projectId}
            buildId={selectedBuildId || undefined}
            onSubmit={(config) => {
              createAgentMutation.mutate(config);
            }}
            onCancel={() => setShowBuilder(false)}
            onTestQuery={async (sql) => {
              if (!projectId || !selectedBuildId) {
                return { columns: [], rows: [], error: 'No build selected' };
              }
              try {
                const result = await testQuery(projectId, selectedBuildId, sql);
                return {
                  columns: result.columns,
                  rows: result.rows,
                  error: result.error,
                };
              } catch (err) {
                return { columns: [], rows: [], error: (err as Error).message };
              }
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Knowledge Base Viewer */}
      {projectId && (
        <KnowledgeViewer
          projectId={projectId}
          open={showKnowledgeViewer}
          onOpenChange={setShowKnowledgeViewer}
        />
      )}
    </DashboardLayout>
  );
}
